import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as signalR from "@microsoft/signalr";
import axios from "axios";

// Icons
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CarIcon from '@mui/icons-material/DirectionsCar';
import FactoryIcon from '@mui/icons-material/Factory';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import DoorIcon from '@mui/icons-material/SensorDoor';
import OccupiedIcon from '@mui/icons-material/SensorOccupied';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import PestControlRodentIcon from '@mui/icons-material/PestControlRodent';
import GrassIcon from '@mui/icons-material/Grass';
import HongKongNewsTicker from './HongKongNewsTicker';
import FloodIcon from '@mui/icons-material/Flood';

// Images
import disableParking from './images/DisableParking.png';
import FallDetect from './images/FallDetect.png';
import parking from './images/Parking.png';
import normalpump from './images/Pump.png';
import pumpoff from './images/PumpOff.png';
import rubbishbin_blue from './images/RubbishBin_blue.png';
import rubbishbin_orange from './images/RubbishBin_orange.png';
import rubbishbin_green from './images/RubbishBin_green.png';
import rubbishbin_yellow from './images/RubbishBin_yellow.png';
import rubbishbin_lg from './images/RubbishBin_light_grey.png';
import rubbishbin from './images/RubbishBin.png';
import rubbishbin10 from './images/RubbishBin_10.png';
import rubbishbin20 from './images/RubbishBin_20.png';
import rubbishbin30 from './images/RubbishBin_30.png';
import rubbishbin40 from './images/RubbishBin_40.png';
import rubbishbin50 from './images/RubbishBin_50.png';
import rubbishbin60 from './images/RubbishBin_60.png';
import rubbishbin70 from './images/RubbishBin_70.png';
import rubbishbin80 from './images/RubbishBin_80.png';
import rubbishbin90 from './images/RubbishBin_90.png';
import tanknormal from './images/WaterTankNormal.png';
import controlpanel from './images/controlpanel.png';
import firealarm from './images/firealarm.png';
import elevator from './images/elevator.png';
import busbar from './images/temperature-control.png';
import flood from './images/FloodBlue.png';
import switchboard from './images/switchboard.png';
import groundFloor from './images/FuTipMap_720px.png';
import cctv from './images/cctv.png';
import humidity from './images/humidity.png';
import dooropen464 from './images/dooropen464_brown.png';
import icon from './images/icons8-place-marker-48.png';
import iconhover from './images/icons8-place-marker.gif';
import ppeople from './images/ppeople.png';
import waterdrop from './images/waterdrop.png';
import Rodent from './images/Rodent.png';
import Irrigation from './images/irrigation.png';
import PMeter from './images/PowerMeter.png';
import carParkFlooding from './images/CarParkFlooding.png';
import fallSensor from './images/FallDetect.png';

// Sounds
import chime from './chime.mp3';
import redTick from './images/RedTick.png';
import click from './shortclick.mp3';
import waterdropsound from './water-droplet.mp3';
import fallsiren from './Ambulance30s.mp3';
import cameraAlarm from './CameraAlarm.mp3';

// Constants
const FLOOR_WIDTH = 740;
const LARGE_CELL_TEXT_SIZE = "30px";
const WATER_LEAKAGE_TYPE = 4;

// Add to your CSS or style component
const globalStyles = `
  @keyframes ticker {
    0% { transform: translateX(0); }
    100% { transform: translateX(-100%); }
  }
`;

const Map = ({ location, selectedFloor }) => {
    // Add this ref to track initial load
    const isInitialLoad = useRef(true);
    const [isLoading, setIsLoading] = useState(true);
    // Refs
    const tindex = useRef(16);

    // Alarm state
    const [alarmPlaying, setAlarmPlaying] = useState(false);
    const alarmaudio = useRef(new Audio(cameraAlarm));

    const doorStatus = useRef(0);
    const vibrationStatus = useRef(0);
    const occupancyStatus = useRef(0);
    const pointLeakStatus = useRef(0);
    const stripLeakStatus = useRef(0);
    const capLeakStatus = useRef(0);
    const binStatus = useRef(0);
    const carStatus = useRef(0);
    const carParkStatus = useRef(0);
    const panelStatus = useRef(0);
    const firePanelStatus = useRef(0);
    const leakStatus = useRef(0);
    const ratStatus = useRef(0);
    const fallSensorStatus = useRef(0);
    const [fallSensorAggregated, setFallSensorAggregated] = useState(0);

    const tArray = useRef([]);

    // State
    const [content, setContent] = useState('Initial content');
    const [retry, setRetry] = useState(false);
    const [dev, setDev] = useState([]);
    const [displaydev, setDisplsyDev] = useState([]);
    const [hoveredCell, setHoveredCell] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [selectedType, setSelectedType] = useState(2);
    const [pins_g, setPins_g] = useState([]);
    const [notifications, setNotifications] = useState([]); // Changed to state for reactivity

    // Navigation
    const navigate = useNavigate();

    // SignalR connection
    const connection = new signalR.HubConnectionBuilder()
        .withUrl("/devicehub")
        .build();

    // Helper functions
    const playClickSound = () => {
        const audio = new Audio(click);
        audio.play();
        audio.onended = () => { }
    };

    // Function to check if any alarm condition is met and control the alarm sound
    const checkAlarmConditions = () => {
        const hasAlarm =
            doorStatus.current > 0 ||
            vibrationStatus.current > 0 ||
            occupancyStatus.current > 0 ||
            pointLeakStatus.current > 0 ||
            stripLeakStatus.current > 0 ||
            binStatus.current > 0 ||
            panelStatus.current > 0 ||
            firePanelStatus.current > 0 ||
            carStatus.current > 0 ||
            carParkStatus.current > 0 ||
            ratStatus.current > 0 ||
            fallSensorStatus.current > 0;

        console.log("Alarm check - Has alarm:", hasAlarm, {
            door: doorStatus.current,
            vibration: vibrationStatus.current,
            occupancy: occupancyStatus.current,
            pointLeak: pointLeakStatus.current,
            stripLeak: stripLeakStatus.current,
            bin: binStatus.current,
            panel: panelStatus.current,
            firePanel: firePanelStatus.current,
            car: carStatus.current,
            carStatus: carParkStatus.current,
            rat: ratStatus.current,
        });

        if (hasAlarm && !alarmPlaying) {
            console.log("Starting alarm sound");
            setAlarmPlaying(true);
        } else if (!hasAlarm && alarmPlaying) {
            console.log("Stopping alarm sound");
            setAlarmPlaying(false);
        }
    };

    // Handle button click
    const handleClick = (e) => {
        playClickSound();
        setSelectedType(e);
        tindex.current = e;

        let filteredDev;

        filteredDev = dev.filter(d => d.data_type === e && d.active === 1);

        console.log(filteredDev); 

        setDisplsyDev(filteredDev);
    };

    // Safe SignalR message sending
    const sendMessage = async () => {
        try {
            // Ensure connection is started
            if (connection.current.state === signalR.HubConnectionState.Disconnected) {
                await connection.current.start();
            }

            // Only send if connected
            if (connection.current.state === signalR.HubConnectionState.Connected) {
                await connection.current.invoke("ReceiveDeviceData");
            } else {
                console.warn("Connection not ready:", connection.current.state);
            }
        } catch (err) {
            console.error("Error sending message:", err);
        }
    };

    const handleDelete = async (notification) => {
        try {
            // Delete from server
            await fetch(`https://futipiot.ddns.net/api/futippastrecord/${notification.Id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
            });

            // Update UI immediately
            setNotifications(prev => prev.filter(n => n.Id !== notification.Id));

            playClickSound();
            await sendMessage(); // Refresh device data
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    // Component functions
    const FloorplanImage = ({ src, alt }) => (
        <div className="estate-image" align-items="center">
            <img src={src} alt={alt} width={FLOOR_WIDTH} />
        </div>
    );

    const PinIcon = ({ id, x, y, src, hoverSrc, scale, type }) => {
        const [isHovered, setIsHovered] = useState(false);

        return (
            <div style={{
                position: "absolute",
                left: `${x}px`,
                top: `${y}px`,
                transform: "translate(-50%, -50%)",
                zIndex: 5,
            }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {(isHovered||type) && (
                    <div style={{
                        position: "absolute",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(255, 0, 0, 0.5)",
                        border: "0px solid rgba(255, 0, 0, 0.8)",
                        animation: "pulse 2s infinite",
                        transform: "translate(-50%, -50%)",
                    }} />
                )}

                <div style={{
                    position: "absolute",
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: "red",
                    transform: "translate(-50%, -50%)",
                }} />
            </div>
        );
    };

    const ImageWithPins = ({ pins }) => (
        <div>
            {pins.map((pin) => (
                <PinIcon
                    key={`${pin.x},${pin.y}`}
                    x={pin.x}
                    y={pin.y}
                    src={icon}
                    type={pin.type === 0 ? false : true}
                />
            ))}
        </div>
    );

    const BinIcon = ({ level, src }) => {
        const binImages = [
            rubbishbin, rubbishbin10, rubbishbin20, rubbishbin30, rubbishbin40,
            rubbishbin50, rubbishbin60, rubbishbin70, rubbishbin80, rubbishbin90
        ];

        const binIndex = Math.min(Math.floor(level / 10), 9);

        return (
            <div style={{ position: 'relative', width: 'fit-content' }}>
                <img src={binImages[binIndex]} alt={`rubbishbin${binIndex * 10}`} width="60px" />
                <img
                    src={src}
                    alt="Overlay"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: 'auto',
                    }}
                />
            </div>
        );
    };

    const cellColor = (alive) => {
        switch (alive) {
            case null:
            case 0: return "#111111";
            case 1: return "#520000";
            case 2: return "#525252";
            default: return "#525252";
        }
    };

    const cellColors = [
        "#803800", "#5f6700", "#003409", "#001781",
        "#803800", "#5f6700", "#003409", "#001781"
    ];

    const cellColors_png = [
        rubbishbin_orange, rubbishbin_yellow, rubbishbin_green, rubbishbin_blue
    ];

    // Navigation handlers
    const handlePanelClick = () => navigate('/panels');
    const handleDoorClick = () => navigate('/doors');
    const handleSwitchBoardWLClick = () => navigate('/leakage');
    const handleElevatorPanelClick = () => navigate('/powermeterpanels');
    const handleWaterFlowMeterslClick = () => navigate('/waterflowmeters');
    const handleFirePanelClick = () => navigate('/firepanels');
    const handleRubbishBinsClick = () => navigate('/rubbishbins');
    const handleWaterLeakgeWLClick = () => navigate('/leakage');
    const handleRatDetectClick = () => navigate('/ratdetect');
    const handleCarParkClick = () => navigate('/leakage');
    const handleEnvironmentsClick = () => navigate('/environments');
    const handleFallsensorsClick = () => navigate('/fallsensors');

    // Data fetching
    useEffect(() => {
        const httpClient = axios.create({
            baseURL: 'https://futipiot.ddns.net', 
            headers: { 'Content-Type': 'application/json' }
        });

        // Call the web API endpoint with GET method
        const getData = async () => {
            setIsLoading(true);
            try {
                const response = await httpClient.get('/api/meters');
                const deviceDetails = response.data.map((item) => ({
                    id: item.deviceId,
                    deviceName: item.deviceName,
                    deviceEui: item.deviceEui,
                    label: item.label,
                    status: item.status,
                    mapx: item.mapx === null ? null : item.mapx * FLOOR_WIDTH / 720,
                    mapy: item.mapy === null ? null : item.mapy * FLOOR_WIDTH / 720,
                    type: item.type,
                    buildingId: item.buildingId,
                    locationId: item.locationId,
                    data_type: item.dataType,
                    alive: item.alive,
                    data: item.data,
                    channel: item.channel,
                    active: item.active
                }));

                const scaledPin_2 = deviceDetails
                    .filter(device => (device.id > 36 && device.id < 124) || (device.id > 163 && device.id < 172))
                    .filter(device => device.mapx !== null && device.mapy !== null)
                    .map(device => ({ ...device, x: device.mapx, y: device.mapy }));

                setPins_g(scaledPin_2);
                setDev(deviceDetails);
                setSelectedType(16);

                // Update status counts
                doorStatus.current = deviceDetails.filter(d => d.data_type === 2 && d.active === 1 && d.type === 1).length;
                vibrationStatus.current = deviceDetails.filter(d => d.data_type === 3 && d.active === 1 && d.type === 1).length;
                occupancyStatus.current = deviceDetails.filter(d => d.data_type === 4 && d.active === 1 && d.type === 1).length;
                pointLeakStatus.current = deviceDetails.filter(d => d.data_type === 6 && d.active === 1 && d.type === 1).length;
                stripLeakStatus.current = deviceDetails.filter(d => (d.data_type === 7 || d.data_type === 12) && d.active === 1 && d.type === 1).length;
                capLeakStatus.current = deviceDetails.filter(d => d.data_type === 31 && d.active === 1 && d.type === 1).length;
                binStatus.current = deviceDetails.filter(d => d.data_type === 9 && parseFloat(d.status) > 75).length;
                panelStatus.current = deviceDetails.filter(d => d.data_type === 13 && d.active === 1 && d.locationId === 0 && d.type === 1).length;
                firePanelStatus.current = deviceDetails.filter(d => d.data_type === 13 && d.locationId === 1 && d.active === 1 && d.type === 1).length; 
                carStatus.current = deviceDetails.filter(d => d.data_type === 16 && d.active === 1 && d.type === 1).length;
                carParkStatus.current = deviceDetails.filter(d => d.data_type === 22 && d.active === 1 && d.type === 1).length;
                ratStatus.current = deviceDetails.filter(d => d.data_type === 20 && d.active === 1 && d.type === 1).length;
                // After existing status calculations, add:
                const fallSensorsAbnormal = deviceDetails.filter(d => d.data_type === 24 && d.status === "異常").length;
                fallSensorStatus.current = fallSensorsAbnormal > 0 ? 1 : 0;
                setFallSensorAggregated(fallSensorStatus.current);

                // Check alarm conditions after updating status
                checkAlarmConditions();

                const filteredDev = tindex.current === 7
                    ? deviceDetails.filter(d => (d.data_type === 7 || d.data_type === 12) && d.active === 1)
                    : deviceDetails.filter(d => d.data_type === tindex.current && d.active === 1);

                setDisplsyDev(filteredDev);

                setIsLoading(false);

            } catch (error) {
                console.error(error);
                setRetry(true);
            }
        };

        const getNotice = async () => {
            try {
                const response = await httpClient.get('/api/futippastrecord/');
                const mArray = response.data.map((item) => ({
                    Id: item.item,
                    Label: item.label,
                    Status: item.status,
                    DataType: item.dataType,
                    Timestamp: item.timestamp,
                    Acknowledge: item.acknowledge,
                    Name: item.name,
                    //Building: item.buildingId
                }));
                console.log("Get Notifications...");
                console.log(mArray);
                setNotifications(mArray);
            } catch (error) {
                console.error(error);
                setRetry(true);
            }

        };

        getData();
        getNotice();
    }, [retry]);

    useEffect(() => {
        const httpClient = axios.create({
            baseURL: 'https://futipiot.ddns.net', // The base URL of your web application
            headers: { 'Content-Type': 'application/json' }
        });

        const getData = async () => {
            try {
                const response = await httpClient.get('/api/meters');
                const deviceDetails = response.data.map((item) => ({
                    id: item.deviceId,
                    deviceName: item.deviceName,
                    deviceEui: item.deviceEui,
                    label: item.label,
                    status: item.status,
                    mapx: item.mapx === null ? null : item.mapx * FLOOR_WIDTH / 720,
                    mapy: item.mapy === null ? null : item.mapy * FLOOR_WIDTH / 720,
                    type: item.type,
                    buildingId: item.buildingId,
                    locationId: item.locationId,
                    data_type: item.dataType,
                    alive: item.alive,
                    data: item.data,
                    channel: item.channel,
                    active: item.active
                }));

                //let filteredDev = deviceDetails.filter(d => d.channel === 1);


                //setPins_g(scaledPin_2);
                setDev(deviceDetails);
                //console.log(deviceDetails);

                // Update status counts
                doorStatus.current = deviceDetails.filter(d => d.data_type === 2 && d.active === 1 && d.type === 1).length;
                vibrationStatus.current = deviceDetails.filter(d => d.data_type === 3 && d.active === 1 && d.type === 1).length;
                occupancyStatus.current = deviceDetails.filter(d => d.data_type === 4 && d.active === 1 && d.type === 1).length;
                pointLeakStatus.current = deviceDetails.filter(d => d.data_type === 6 && d.active === 1 && d.type === 1).length;
                stripLeakStatus.current = deviceDetails.filter(d => (d.data_type === 7 || d.data_type === 12) && d.active === 1 && d.type === 1).length;
                capLeakStatus.current = deviceDetails.filter(d => d.data_type === 31 && d.active === 1 && d.type === 1).length;
                binStatus.current = deviceDetails.filter(d => d.data_type === 9 && parseFloat(d.status) > 75).length;
                panelStatus.current = deviceDetails.filter(d => d.data_type === 13 && d.active === 1 && d.locationId === 0 && d.type === 1).length;
                firePanelStatus.current = deviceDetails.filter(d => d.data_type === 13 && d.locationId === 1 && d.active === 1 && d.type === 1).length;
                carStatus.current = deviceDetails.filter(d => d.data_type === 16 && d.active === 1 && d.type === 1).length;
                carParkStatus.current = deviceDetails.filter(d => d.data_type === 22 && d.active === 1 && d.type === 1).length;
                ratStatus.current = deviceDetails.filter(d => d.data_type === 20 && d.active === 1 && d.type === 1).length;
                // After existing status calculations, add:
                const fallSensorsAbnormal = deviceDetails.filter(d => d.data_type === 24 && d.status === "異常").length;
                fallSensorStatus.current = fallSensorsAbnormal > 0 ? 1 : 0;
                setFallSensorAggregated(fallSensorStatus.current);

                // Check alarm conditions after updating status
                checkAlarmConditions();

                const filteredDev = tindex.current === 7
                    ? deviceDetails.filter(d => (d.data_type === 7 || d.data_type === 12) && d.active === 1)
                    : deviceDetails.filter(d => d.data_type === tindex.current && d.active === 1);
                //console.log(filteredDev); 

                setDisplsyDev(filteredDev);
                if (isInitialLoad.current) {
                    isInitialLoad.current = false;
                }

                try {
                    const response = await httpClient.get('/api/futippastrecord/');
                    const mArray = response.data.map((item) => ({
                        Id: item.item,
                        Label: item.label,
                        Status: item.status,
                        DataType: item.dataType,
                        Timestamp: item.timestamp,
                        Acknowledge: item.acknowledge,
                        Name: item.name,
                        //Building: item.buildingId
                    }));

                    setNotifications(mArray);
                } catch (error) {
                    console.error(error);
                    setRetry(true);
                }
            } catch (error) {
                console.error(error);
            }
        };

        connection
            .start()
            .then(() => console.log("Connected to the hub"))
            .catch((err) => console.error(err));

        connection.on("ReceiveDeviceData", () => {
            console.log("Updating....");
            getData();
        });

        connection.on("ReceiveFallSensorUpdate", () => {
            // Re-fetch meters or recalculate aggregated status
            getData(); // assuming getData is defined and accessible
        });

        //return () => {
        //    connection.off("ReceiveDeviceData");
        //};
    }, []);

    // UI Components
    const LargeCell = ({ title, icon, image, status, statusText, onClick, delay }) => (
        <td
            className="large-cellstyle"
            onClick={onClick}
            style={{
                animation: isInitialLoad.current ? "slideIn 0.5s ease-out forwards" : "none",
                animationDelay: isInitialLoad.current ? `${delay}s` : "0s",
                opacity: isInitialLoad.current ? 0 : 1
            }}
        >
            <div style={{ color: "#f7f4ef", backgroundColor: "#676767" }}>
                {icon} {title}
            </div>
            <hr style={{ height: "2px", visibility: "hidden", marginBottom: "0px" }} />
            <div style={{ textAlign: "center" }}>
                {typeof image === 'string' ? (
                    <img src={image} alt={title} height="56" />
                ) : (
                    image
                )}
            </div>
            <div
                className="lgstatusstyle"
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: LARGE_CELL_TEXT_SIZE,
                    fontWeight: "bold",
                    color: "#f5f0e9",
                    backgroundColor: status === 0 ? "#006ec8" : "red"
                }}
            >
                {statusText || (status === 0 ? "正常" : "部分故障")}
            </div>
        </td>
    );

    const DeviceList = () => {
        if (tindex.current === 9) {
            return (
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px' }}>
                    <tbody>
                        <tr>
                            {/* Building 1 */}
                            <td style={{ verticalAlign: 'top' }}>
                                <BuildingBinSection
                                    title="斑蝶樓回收箱"
                                    buildingId={1}
                                    devices={displaydev}
                                    colors={cellColors_png}
                                />
                            </td>


                        ))}
                        </tr>
                    </tbody>
                </table>
            );
        } else if (tindex.current === 2) {
            return (
                <div>
                    <tr className="row">
                        {Array.isArray(displaydev) &&
                            displaydev.map((device, index) => (
                                <DeviceCell
                                    key={index}
                                    device={device}
                                    index={index}
                                    hoveredCell={hoveredCell}
                                    cellStyle={tindex.current === 2 ? "doorcellstyle" : "bincellstyle"}
                                />
                            ))
                        }
                    </tr>
                </div>
            );
        } else {
            return (
                <div>
                    <tr className="row">
                        {Array.isArray(displaydev) &&
                            displaydev.map((device, index) => (
                                <DeviceCell
                                    key={index}
                                    device={device}
                                    index={index}
                                    hoveredCell={hoveredCell}
                                    cellStyle="cellstyle"
                                />
                            ))
                        }
                    </tr>
                </div>
            );
        }
    };



    const ctext = (p) => {
        //console.log(p);
        switch (p) {
            case 1:
                return "#f7f4ef";
                
            case 2:
                return "#f7f4ef";
                
            case 3:
                return "#f7f4ef";
                
            case 4:
                return "#f7f4ef";
                
            default:
                break;
        }
    };

    const cbackground = (p) => {
        //console.log(p);
        switch (p) {
            case 1:
                return "#7f7f7f";

            case 2:
                return "#c97500";

            case 3:
                return "#c800c8";

            case 4:
                return "#42a5f5";

            default:
                break;
        }
    };

    const NList = () => {
        return (
            <div>
                {notifications.map((notification) => (
                    <tr className="row" key={notification.Id}>
                        <td className="notice-cell" style={{ fontSize: "20px", overflow: "hidden", fontWeight: "bold", color: ctext(notification.DataType), backgroundColor: cbackground(notification.DataType) }}>
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '4px',
                                    backgroundColor: '#c97500',
                                    marginRight: '8px'
                                }}
                            ></span>
                            {notification.Label}, {notification.Timestamp}
                            {notification.Status}
                        </td>
                        <td className="delete-button">
                            <img
                                src={redTick}
                                alt="redtick"
                                width="30px"
                                onClick={() => handleDelete(notification)}
                            />
                        </td>
                    </tr>
                ))}
            </div>
        );
    };


    const BuildingBinSection = ({ title, buildingId, devices, colors }) => (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            backgroundColor: '#555555',
            borderRadius: '12px',
            padding: '4px',
            margin: '4px 0'
        }}>
            <h3 style={{
                margin: '0 0 8px 0',
                color: '#676767',
                fontSize: '18px',
                fontWeight: '600'
            }}>{title}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {devices
                    .filter(dev_item => dev_item.buildingId === buildingId)
                    .map((dev_item, index) => (
                        <td
                            key={index}
                            className="bincellstyle"
                            style={{
                                fontSize: "16px",
                                overflow: "hidden",
                                color: "#f7f4ef",
                                backgroundColor: "#525252",
                            }}
                        >
                            <table>
                                <td>{dev_item.label}</td>
                                <td>
                                    <br />
                                    <BinIcon level={parseInt(dev_item.status, 10)} src={colors[index % 4]} />
                                </td>
                            </table>
                        </td>
                    ))
                }
            </div>
        </div>
    );

    const DeviceCell = ({ device, index, hoveredCell, cellStyle }) => (
        <td
            key={index}
            className={cellStyle}
            style={{
                fontSize: "16px",
                overflow: "hidden",
                color: "#f7f4ef",
                backgroundColor: "#525252",
            }}
        >
            <table>
                <tr style={{ height: "85px" }}>{device.label}</tr>
                <tr>
                    <div style={{
                        fontSize: "18px",
                        overflow: "hidden",
                        fontWeight: "bold",
                        color: device.type === 1 ? hoveredCell === index ? "#f5f0e9" : "red" : hoveredCell === index ? "#f5f0e9" : "green"
                    }}>
                        <div
                            className="statusstyle"
                            style={{
                                marginTop: "auto",
                                textAlign: "center",
                                fontWeight: "bold",
                                color: "#f5f0e9",
                                backgroundColor: device.type === 1 ? "red" : "#006ec8"
                            }}
                        >
                            {device.status}
                        </div>
                    </div>
                </tr>
            </table>
        </td>
    );

    const SidebarButton = ({ type, label }) => (
        <div
            className="buttonstyle"
            style={{
                fontSize: "15px",
                color: selectedType === type ? "#202020" : "#f5f1eb",
                backgroundColor: selectedType === type ? "#BfBfBf" : "#4f4f4f"
            }}
            onClick={() => handleClick(type)}
        >
            {label}
        </div>
    ); 

    const NewsTicker = () => {
        const [newsItems, setNewsItems] = useState([]);
        const tickerRef = useRef(null);

        useEffect(() => {
            // Fetch news data (you can replace this with a real API call)
            const fetchNews = async () => {
                try {
                    // Example news data - replace with actual API call
                    const exampleNews = [
                        "Breaking: Major tech company announces new AI breakthrough",
                        "Global markets show mixed results in today's trading",
                        "Scientists discover potential new renewable energy source",
                        "Weather alert: Severe storms expected in several regions",
                        "Sports: Underdog team wins championship in stunning upset"
                    ];

                    setNewsItems(exampleNews);

                    // For a real implementation, you might use something like:
                    // const response = await axios.get('https://newsapi.org/v2/top-headlines?country=us&apiKey=YOUR_API_KEY');
                    // setNewsItems(response.data.articles.map(article => article.title));
                } catch (error) {
                    console.error("Error fetching news:", error);
                }
            };

            fetchNews();
        }, []);

        return (
            <div
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#333',
                    color: 'white',
                    padding: '10px 0',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 -2px 5px rgba(0,0,0,0.2)'
                }}
            >
                <div
                    ref={tickerRef}
                    style={{
                        display: 'inline-block',
                        paddingLeft: '100%',
                        animation: 'ticker 30s linear infinite'
                    }}
                >
                    {newsItems.map((item, index) => (
                        <span
                            key={index}
                            style={{
                                paddingRight: '50px',
                                fontSize: '20px'
                            }}
                        >
                            {item} •
                        </span>
                    ))}
                </div>

                {/* Add the animation to your CSS */}
                <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
            </div>
        );
    };

    return (
        <div className="containerstyle">
            <div>
                <table style={{ verticalAlign: "top" }}>
                    <tbody>
                        <tr className="row">
                            <td style={{ verticalAlign: "top", width: "760px" }}>
                                <table style={{ verticalAlign: "top" }}>
                                    <tbody>
                                        <tr>
                                            {!isLoading && (
                                                <div className="floorpin">
                                                    <FloorplanImage src={groundFloor} alt="Ground Floor" />
                                                    <ImageWithPins pins={pins_g} />
                                                </div>
                                            )}
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                            <td style={{ verticalAlign: "top", width: "640px" }}>
                                {!isLoading && (
                                    <table style={{ verticalAlign: "top" }}>
                                        <tbody>
                                            <tr className="row">
                                                <LargeCell
                                                    title="車路阻塞監察"
                                                    icon={<CarIcon color="primary" />}
                                                    image={parking}
                                                    status={carStatus.current}
                                                    statusText={carStatus.current === 0 ? "暢通" : "阻塞"}
                                                    onClick={() => handleClick(16)}
                                                    delay={0.1}
                                                />
                                                <LargeCell
                                                    title="停車埸水浸監察"
                                                    icon={<FloodIcon color="primary" />}
                                                    image={carParkFlooding}
                                                    status={carParkStatus.current}
                                                    statusText={carParkStatus.current === 0 ? "正常" : "部分已開"}
                                                    onClick={handleCarParkClick}
                                                    delay={0.3}
                                                />
                                                <LargeCell
                                                    title="門/通道"
                                                    icon={<DoorIcon color="primary" />}
                                                    image={dooropen464}
                                                    status={doorStatus.current}
                                                    statusText={doorStatus.current === 0 ? "正常" : "部分已開"}
                                                    onClick={handleDoorClick}
                                                    delay={0.3}
                                                />
                                                {/*<LargeCell
                                                    title="用水量監察"
                                                    icon={<DoorIcon color="primary" />}
                                                    image={flood}
                                                    status={0}
                                                    statusText="正常"
                                                    onClick={handleWaterFlowMeterslClick}
                                                    delay={0.3}
                                                />*/}
                                                {/*<LargeCell
                                                    title="回收箱"
                                                    icon={<DeleteOutlineIcon color="primary" />}
                                                    image={rubbishbin}
                                                    status={binStatus.current}
                                                    statusText={binStatus.current === 0 ? "正常" : "部份已滿"}
                                                    onClick={handleRubbishBinsClick}
                                                    delay={0.3}
                                                />*/}
                                            </tr>
                                            <tr className="row">
                                                <LargeCell
                                                    title="泵房Panel"
                                                    icon={<OilBarrelIcon color="primary" />}
                                                    image={controlpanel}
                                                    status={panelStatus.current}
                                                    onClick={handlePanelClick}
                                                    delay={0.4}
                                                />
                                                <LargeCell
                                                    title="鼠蹤檢測"
                                                    icon={<PestControlRodentIcon color="primary" />}
                                                    image={Rodent}
                                                    status={ratStatus.current}
                                                    statusText={ratStatus.current === 0 ? "正常" : "有移動"}
                                                    delay={0.5}
                                                />
                                                <LargeCell
                                                    title="漏水檢測"
                                                    icon={<WaterDropIcon color="primary" />}
                                                    image={waterdrop}
                                                    status={pointLeakStatus.current}
                                                    statusText={pointLeakStatus.current === 0 ? "正常" : "漏水"}
                                                    onClick={handleWaterLeakgeWLClick}
                                                    delay={0.6}
                                                />
                                            </tr>
                                            <tr className="row">
                                                {/*<LargeCell
                                                    title="制櫃頂水濕"
                                                    icon={<WaterDropIcon color="primary" />}
                                                    image={
                                                        <div style={{ position: 'relative' }}>
                                                            <img src={switchboard} alt="switchboard" height="56" />
                                                            <img
                                                                src={waterdrop}
                                                                alt="Overlay"
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 90,
                                                                    left: 90,
                                                                    width: '10%',
                                                                    height: 'auto',
                                                                }}
                                                            />
                                                        </div>
                                                    }
                                                    status={0}
                                                    statusText="正常"
                                                    onClick={handleSwitchBoardWLClick}
                                                    delay={0.7}
                                                />*/}
                                                <LargeCell
                                                    title="消防Panel"
                                                    icon={<OilBarrelIcon color="primary" />}
                                                    image={firealarm}
                                                    status={0}
                                                    status={firePanelStatus.current}
                                                    onClick={handleFirePanelClick}
                                                    delay={0.7}
                                                />
                                                <LargeCell
                                                    title="回收箱"
                                                    icon={<DeleteOutlineIcon color="primary" />}
                                                    image={rubbishbin}
                                                    status={binStatus.current}
                                                    statusText={binStatus.current === 0 ? "正常" : "部份已滿"}
                                                    onClick={handleRubbishBinsClick}
                                                    delay={0.8}
                                                />
                                                <LargeCell
                                                    title="BusBar溫度"
                                                    icon={<OilBarrelIcon color="primary" />}
                                                    image={busbar}
                                                    status={0}
                                                    statusText="正常"
                                                    delay={0.9}
                                                />
                                                {/*<LargeCell
                                                    title="回收箱"
                                                    icon={<DeleteOutlineIcon color="primary" />}
                                                    image={rubbishbin}
                                                    status={binStatus.current}
                                                    statusText={binStatus.current === 0 ? "正常" : "部份已滿"}
                                                    onClick={handleRubbishBinsClick}
                                                    delay={0.9}
                                                />*/}
                                            </tr>
                                            <tr className="row">
                                                <LargeCell
                                                    title="水缸水位檢測"
                                                    icon={<FactoryIcon color="primary" />}
                                                    image={flood}
                                                    status={0}
                                                    statusText="正常"
                                                    delay={1.0}
                                                />
                                                <LargeCell
                                                    title="溫度濕度檢測"
                                                    icon={<FactoryIcon color="primary" />}
                                                    image={humidity}
                                                    status={0}
                                                    statusText="正常"
                                                    onClick={handleEnvironmentsClick}
                                                    delay={1.1}
                                                />
                                                <LargeCell
                                                    title="跌倒檢測"
                                                    icon={<FactoryIcon color="primary" />}
                                                    image={fallSensor}
                                                    status={fallSensorAggregated}
                                                    statusText={fallSensorAggregated === 0 ? "正常" : "異常"}
                                                    onClick={handleFallsensorsClick}
                                                    delay={1.1}
                                                />
                                            </tr>
                                        </tbody>
                                    </table>
                                )}
                            </td>
                            <td style={{ verticalAlign: "top", width: "460px", maxHeight: "750px" }}>
                                <table className="notice-style" style={{ verticalAlign: "top" }} >
                                    <tbody>
                                        <tr>
                                            <td><div style={{ fontSize: "24px", fontWeight: "bold", color: "#f7f4ef" }}>狀態通知</div></td>
                                        </tr>
                                        <tr>
                                            <td><NList /></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
export default Map;